import {WBButton} from '@wandb/weave/common/components/elements/WBButtonNew';
import React, {useCallback, useEffect, useState} from 'react';

import {
  SectionHeaderActions,
  SectionHeaderNew as SectionHeader,
} from '../../components/Section';
import {WBSlateElementType} from '../../components/Slate/WBSlate/types';
import {isEmptySlateValue} from '../../components/Slate/WBSlate/utils';
import {WBSlateReduxBridgeDisabled} from '../../components/Slate/WBSlateReduxBridge';
import {ReportContext, ReportContextValue} from '../../state/reports/context';
import ReportErrorBoundary from '../ReportPage/ReportErrorBoundary';
import {useEntityPageContext, useEntityPageUpdaterContext} from './context';
import {EmptyState} from './EmptyState';
import * as S from './EntityReport.styles';
import {getEntityProfileInitialValue} from './util';

type EntityReportProps = {
  editing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
};

export const EntityReport: React.FC<EntityReportProps> = React.memo(
  ({editing, onEdit, onCancelEdit}) => {
    const {entityName, entityIsTeam, reportSpec, readOnly} =
      useEntityPageContext();
    const {setReportSpec} = useEntityPageUpdaterContext();

    // Local value + saving

    const [localValue, setLocalValue] = useState(
      reportSpec ?? getEntityProfileInitialValue(entityName, entityIsTeam)
    );
    const [flickering, setFlickering] = useState(false);

    const onChange = useCallback((newValue: WBSlateElementType[]) => {
      setLocalValue(newValue);
    }, []);

    const save = useCallback(() => {
      setFlickering(true);
      if (isEmptySlateValue(localValue)) {
        setReportSpec(null);
        setLocalValue(getEntityProfileInitialValue(entityName, entityIsTeam));
      } else {
        setReportSpec(localValue);
      }
      onCancelEdit();
    }, [localValue, entityName, entityIsTeam, setReportSpec, onCancelEdit]);

    useEffect(() => {
      if (flickering) {
        setFlickering(false);
      }
    }, [flickering]);

    // Render helper

    const renderContent = (
      contentValue: WBSlateElementType[],
      contentReadOnly: boolean
    ) => (
      <EntityReportInner
        readOnly={contentReadOnly}
        value={contentValue}
        onChange={onChange}
      />
    );

    return (
      <>
        {reportSpec != null ? (
          <EntityReportPreview>
            {!flickering && renderContent(reportSpec, true)}
          </EntityReportPreview>
        ) : (
          <EmptyState
            iconName="user-profile-personal"
            header={`Introduce yourself to the world`}
            body={`Let others know a little bit about who you are and what you do.`}
            buttonText={`Add an intro`}
            onClickButton={onEdit}
          />
        )}
        {editing && (
          <EntityReportModal onSave={save} onClose={onCancelEdit}>
            {renderContent(localValue, readOnly)}
          </EntityReportModal>
        )}
      </>
    );
  }
);

type EntityReportPreviewProps = {};

const EntityReportPreview: React.FC<EntityReportPreviewProps> = React.memo(
  ({children}) => {
    return <S.Container>{children}</S.Container>;
  }
);

type EntityReportModalProps = {onSave: () => void; onClose: () => void};

const EntityReportModal: React.FC<EntityReportModalProps> = React.memo(
  ({onSave, onClose, children}) => {
    const [warningOpen, setWarningOpen] = useState(false);

    const openWarning = useCallback(() => {
      setWarningOpen(true);
    }, []);

    const closeWarning = useCallback(() => {
      setWarningOpen(false);
    }, []);

    return (
      <S.Modal open onClose={openWarning}>
        <S.ModalHeader>
          <S.ModalSaveButton primary onClick={onSave}>
            Save
          </S.ModalSaveButton>
          <S.ModalCloseIcon name={`close`} onClick={openWarning} />
        </S.ModalHeader>
        {children}
        {warningOpen && (
          <S.ModalWarningOverlay>
            <S.ModalWarningContent>
              <S.ModalWarningText>
                Are you sure you want to exit without saving?
              </S.ModalWarningText>
              <S.ModalWarningActions>
                <S.ModalWarningButton primary onClick={closeWarning}>
                  Resume
                </S.ModalWarningButton>
                <S.ModalWarningButton onClick={onClose}>
                  Exit and discard
                </S.ModalWarningButton>
              </S.ModalWarningActions>
            </S.ModalWarningContent>
          </S.ModalWarningOverlay>
        )}
      </S.Modal>
    );
  }
);

type EntityReportInnerProps = {
  readOnly: boolean;
  value: WBSlateElementType[];
  onChange: (newValue: WBSlateElementType[]) => void;
};

const EntityReportInner: React.FC<EntityReportInnerProps> = React.memo(
  ({readOnly, value, onChange}) => {
    return (
      <ReportErrorBoundary>
        <EntityReportReady
          readOnly={readOnly}
          value={value}
          onChange={onChange}
        />
      </ReportErrorBoundary>
    );
  }
);

const ENTITY_REPORT_CONTEXT_VALUE: ReportContextValue = {
  disableComments: true,
  disableCopyLinkToSection: true,
  disablePanels: true,
  disableTableOfContents: true,
  disableLinkToReport: true,
  disablePersistentTopLine: true,
};

type EntityReportReadyProps = EntityReportInnerProps;

const EntityReportReady: React.FC<EntityReportReadyProps> = ({
  readOnly,
  value,
  onChange,
}) => {
  const {profileViewID, entityName} = useEntityPageContext();

  return (
    <ReportContext.Provider value={ENTITY_REPORT_CONTEXT_VALUE}>
      <WBSlateReduxBridgeDisabled
        readOnly={readOnly}
        entityName={entityName}
        projectName={``}
        viewId={profileViewID}
        value={value}
        onChange={onChange}
      />
    </ReportContext.Provider>
  );
};

type EntityReportSectionHeaderProps = {onEdit: () => void};

export const EntityReportSectionHeader: React.FC<EntityReportSectionHeaderProps> =
  React.memo(({onEdit}) => {
    const {readOnly} = useEntityPageContext();
    return (
      <SectionHeader>
        {!readOnly && (
          <SectionHeaderActions>
            <WBButton size={`icon`} onClick={onEdit}>
              <S.EditIcon onClick={onEdit} />
            </WBButton>
          </SectionHeaderActions>
        )}
      </SectionHeader>
    );
  });
